import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeriesParameter } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
// @ts-ignore Ignore unused commented imports
import { Actions, createEffect, ofType } from '@ngrx/effects';
// @ts-ignore Ignore unused commented ActionTypes import
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { PoultryContextKeys } from 'src/app/shared/constants';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import Utils from '../../../shared/utils';
import {
  BenchmarkData,
  BenchmarkSettings,
  BenchmarkTypes,
  DataViewType,
  DefaultKPIselection,
  FarmerInsightsModuleState,
  FlockDataParameter,
  GlobalFilterSettings,
  KpiGroup,
  LocalFilterSettings,
  NormalizedTimeSeries,
  QueryableFlockData,
  TimeSeriesResponse,
} from '../../models/farmer-insights.model';
import { FlockDataService } from '../../services/flock-data.service';
import * as farmerInsightsActions from '../actions/farmer-insights.actions';
import {
  getFarmerInsightsModuleState,
  getQueryableFlockData,
  getSelectedDataViewType,
} from '../selectors/farmer-insights.selectors';

@Injectable()
export class FarmerInsightsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private flockDataService = inject(FlockDataService);
  private settingsHelper = inject(SettingsServiceHelper);
  private readonly locale = inject(LocaleService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadData),
      withLatestFrom(this.store.select(getFarmerInsightsModuleState)),
      exhaustMap(([_, state]) => {
        if (state.localFilterSettings === null) {
          return of(
            farmerInsightsActions.clearFarmerInsightsState(),
            farmerInsightsActions.loadFarmerInsightsGlobalFilterSettingsRequest(),
            farmerInsightsActions.loadFarmerInsightsBenchmarkSettings(),
            farmerInsightsActions.loadQueryableFlockDataRequest(),
          );
        } else {
          return of(
            farmerInsightsActions.clearFarmerInsightsState(),
            farmerInsightsActions.loadFarmerInsightsGlobalFilterSettingsRequest(),
            farmerInsightsActions.loadFarmerInsightsLocalFilterSettingsRequest(),
            farmerInsightsActions.loadFarmerInsightsBenchmarkSettings(),
            farmerInsightsActions.loadQueryableFlockDataRequest(),
          );
        }
      }),
    ),
  );

  filterChange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        farmerInsightsActions.setLocationsFilter,
        farmerInsightsActions.setCyclesFilter,
        farmerInsightsActions.setBarnsFilter,
        farmerInsightsActions.setSlideInFilters,
        farmerInsightsActions.setDataFilters,
        farmerInsightsActions.setKpisFilter,
        farmerInsightsActions.refreshTimeSeries,
        farmerInsightsActions.setDateRangeFilter,
        farmerInsightsActions.setDetailedView,
        farmerInsightsActions.setSelectedViewMode,
        farmerInsightsActions.setDataViewTypeFilter,
      ),
      withLatestFrom(this.store.select(getFarmerInsightsModuleState)),
      switchMap(([action, state]) => {
        if (state.selectedConceptId !== null && state.queryableFlockData !== null) {
          // the not-selected cycles are actually saved in the settingservice,
          // so the selection must be inverted to get the really selected ids.
          const unselectedCycleIds = <number[]>(
            state.queryableFlockData.cycles
              .map((cycle) => cycle.cycleId)
              .filter((cycleId) => !(<number[]>state.unselectedCycleIds).includes(cycleId))
          );
          const loadTimeSeries = farmerInsightsActions.loadTimeSeriesRequest({
            getFlockDataRequest: {
              locationIds: state.selectedLocationIds,
              cycleIds: unselectedCycleIds,
              barnIds: state.selectedBarnIds,
              parameters: state.selectedKpis,
              dataViewType: state.selectedDataViewType,
              startDate: state.selectedDateRange.startDate,
              endDate: state.selectedDateRange.endDate,
              conceptId: state.selectedConceptId,
            },
          });

          if (action.type === farmerInsightsActions.refreshTimeSeries.type) {
            return of(loadTimeSeries);
          } else {
            const setFarmerInsightsLocalFilterSettings =
              farmerInsightsActions.setFarmerInsightsLocalFilterSettings({
                localFilterSettings: {
                  selectedLocationIds: state.selectedLocationIds,
                  unselectedCycleIds: state.unselectedCycleIds,
                  selectedBarnIds: state.selectedBarnIds,
                  selectedConceptId: state.selectedConceptId,
                  selectedDateRangeStart: state.selectedDateRange.startDate,
                  selectedDateRangeEnd: state.selectedDateRange.endDate,
                  selectedDateRangeDescription: state.selectedDateRange.description,
                },
              });

            const setFarmerInsightsGlobalFilterSettings =
              farmerInsightsActions.setFarmerInsightsGlobalFilterSettings({
                globalFilterSettings: {
                  selectedKpis: state.selectedKpis,
                  selectedDataViewType: state.selectedDataViewType,
                  isDetailedModeSelected: state.isDetailedModeSelected,
                  selectedViewMode: state.selectedViewMode,
                },
              });

            // In case only a 'view' filter is changed, we don't necessarily have to update the data.
            if (
              action.type === farmerInsightsActions.setDetailedView.type ||
              action.type === farmerInsightsActions.setSelectedViewMode.type
            ) {
              return of(
                setFarmerInsightsLocalFilterSettings,
                setFarmerInsightsGlobalFilterSettings,
              );
            } else {
              return of(
                setFarmerInsightsLocalFilterSettings,
                setFarmerInsightsGlobalFilterSettings,
                loadTimeSeries,
              );
            }
          }
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  conceptChange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.setSelectedConceptId),
      withLatestFrom(this.store.select(getFarmerInsightsModuleState)),
      switchMap(([action, state]) => {
        if (action.conceptId !== null && state.queryableFlockData !== null) {
          const unselectedCycleIds = <number[]>(
            state.queryableFlockData.cycles
              .filter((cycle) => cycle.conceptId !== action.conceptId)
              .map((cycle) => cycle.cycleId)
          );

          return of(farmerInsightsActions.setCyclesFilter({ cycleIds: unselectedCycleIds }));
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  benchmarkFilterChange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        farmerInsightsActions.setDataFilters,
        farmerInsightsActions.setKpisFilter,
        farmerInsightsActions.refreshBenchmarkTimeSeries,
        farmerInsightsActions.setDateRangeFilter,
        farmerInsightsActions.setSelectedConceptId,
        farmerInsightsActions.setFarmerInsightsBenchmarkSettings,
      ),
      withLatestFrom(this.store.select(getFarmerInsightsModuleState)),
      exhaustMap(([_, state]) => {
        if (state.selectedConceptId !== null) {
          return of(
            farmerInsightsActions.loadBenchmarkSelectedPeriodRequest({
              getBenchmarksSelectedPeriodRequest: {
                parameters: state.selectedKpis,
                startDate: state.selectedDateRange.startDate,
                endDate: state.selectedDateRange.endDate,
                conceptId: state.selectedConceptId,
              },
            }),
            farmerInsightsActions.loadBenchmarkLastWeeksRequest({
              getBenchmarksLastWeeksRequest: {
                parameters: state.selectedKpis,
                startDate: new Date(new Date().setDate(new Date().getDate() - 70)).toISOString(),
                endDate: Utils.toUtcDate(new Date()).toISOString(),
                conceptId: state.selectedConceptId,
              },
            }),
            farmerInsightsActions.loadBenchmarkCustomerAverageSelectedPeriodRequest({
              getBenchmarkCustomerAverageSelectedPeriodRequest: {
                parameters: state.selectedKpis,
                startDate: state.selectedDateRange.startDate,
                endDate: state.selectedDateRange.endDate,
                conceptId: state.selectedConceptId,
              },
            }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  loadQueryableFlockData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadQueryableFlockDataRequest),
      switchMap(() =>
        combineLatest([this.store.select(getFarmerInsightsModuleState)]).pipe(
          skipWhile(
            ([state]) =>
              state.localFilterSettings === undefined && state.globalFilterSettings === undefined,
          ),
          take(1),
        ),
      ),
      mergeMap(([state]) =>
        this.flockDataService.getQueryableFlockData().pipe(
          switchMap((queryableFlockData: QueryableFlockData) => {
            let localFilterSettings: LocalFilterSettings = Object.assign(
              {},
              state.localFilterSettings,
            );
            let globalFilterSettings: GlobalFilterSettings = Object.assign(
              {},
              state.globalFilterSettings,
            );

            if (!state.localFilterSettings?.selectedConceptId) {
              localFilterSettings = {
                selectedLocationIds: queryableFlockData.locations.map(
                  (location) => location.locationId,
                ),
                unselectedCycleIds: queryableFlockData.cycles.map((cycle) => cycle.cycleId),
                selectedBarnIds: queryableFlockData.barns.map((barn) => barn.barnId),
                selectedConceptId:
                  queryableFlockData.concepts.length > 0
                    ? queryableFlockData.concepts[0].conceptId
                    : 0,
                selectedDateRangeStart: null,
                selectedDateRangeEnd: null,
                selectedDateRangeDescription: null,
              };
            }

            if (
              globalFilterSettings === null ||
              (globalFilterSettings?.selectedKpis?.length === 0 &&
                globalFilterSettings?.selectedDataViewType === null)
            ) {
              globalFilterSettings = {
                selectedKpis: DefaultKPIselection,
                selectedDataViewType: DataViewType.PerCycle,
                isDetailedModeSelected: state.isDetailedModeSelected,
                selectedViewMode: state.selectedViewMode,
              };
            }

            let kpiGroups: KpiGroup[] = [];
            if (queryableFlockData) {
              queryableFlockData?.parameters?.forEach((param) => {
                const kpiGroupName: string = <string>(<unknown>param.properties.KpiGrouping);
                let existingGroup = kpiGroups.find((k) => k.name === kpiGroupName);
                if (!existingGroup) {
                  existingGroup = {
                    name: kpiGroupName,
                    order: <number>(<unknown>param.properties.KpiGroupingOrder),
                    children: [],
                  };
                  kpiGroups.push(existingGroup);
                }
                existingGroup.children.push({
                  kpi: FlockDataParameter[param.code as keyof typeof FlockDataParameter],
                  order: <number>(<unknown>param.properties.Order),
                });

                existingGroup.children = existingGroup.children.sort((a, b) =>
                  a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
                );
              });

              kpiGroups = kpiGroups.sort((a, b) =>
                a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
              );
            }

            return of(
              farmerInsightsActions.loadQueryableFlockDataSuccess({
                queryableFlockData,
                localFilterSettings,
                globalFilterSettings,
              }),
              farmerInsightsActions.loadkpiGroups({ kpiGroups }),
              farmerInsightsActions.refreshTimeSeries(),
              farmerInsightsActions.refreshBenchmarkTimeSeries(),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(farmerInsightsActions.loadQueryableFlockDataFailed({ error: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadTimeSeries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadTimeSeriesRequest),
      exhaustMap((getFlockDataRequest) =>
        this.flockDataService.getFlockData(getFlockDataRequest.getFlockDataRequest).pipe(
          switchMap((flockDataResponse: TimeSeriesResponse) =>
            of(
              farmerInsightsActions.loadTimeSeriesSuccess({ timeSeries: flockDataResponse.data }),
              farmerInsightsActions.convertToNormalizedTimeSeriesArray({
                timeSeries: flockDataResponse.data,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(
                farmerInsightsActions.loadTimeSeriesSuccess({ timeSeries: null }),
                farmerInsightsActions.setNormalizedTimeSeriesArray({
                  normalizedTimeSeriesArray: [],
                }),
              );
            }
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            return of(
              farmerInsightsActions.loadTimeSeriesFailed({ error: error.message }),
              NotificationActions.errorSnackbar({ message: error.message }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadBenchmarkSelectedPeriod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadBenchmarkSelectedPeriodRequest),
      exhaustMap((request) =>
        this.flockDataService.getBenchmarks(request.getBenchmarksSelectedPeriodRequest).pipe(
          switchMap((timeSeriesResponse: TimeSeriesResponse) =>
            of(
              farmerInsightsActions.loadBenchmarkTimeSeriesSuccess({
                timeSeries: timeSeriesResponse.data,
              }),
              farmerInsightsActions.convertToNormalizedBenchmarkData({
                benchmarkType: BenchmarkTypes.SelectedPeriod,
                timeSeries: timeSeriesResponse.data,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(
                farmerInsightsActions.loadBenchmarkTimeSeriesSuccess({
                  timeSeries: null,
                }),
                farmerInsightsActions.setNormalizedBenchmarkSelectedPeriodData({
                  normalizedBenchmarkData: null,
                }),
              );
            }
            return of(
              farmerInsightsActions.loadBenchmarkTimeSeriesFailed({ error: error.message }),
              NotificationActions.errorSnackbar({ message: error.message }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  loadBenchmarkLastWeeks$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadBenchmarkLastWeeksRequest),
      exhaustMap((action) =>
        this.flockDataService.getBenchmarks(action.getBenchmarksLastWeeksRequest).pipe(
          switchMap((timeSeriesResponse: TimeSeriesResponse) =>
            of(
              farmerInsightsActions.loadBenchmarkLastWeeksSuccess({
                timeSeries: timeSeriesResponse.data,
              }),
              farmerInsightsActions.convertToNormalizedBenchmarkData({
                benchmarkType: BenchmarkTypes.LastWeeks,
                timeSeries: timeSeriesResponse.data,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(
                farmerInsightsActions.loadBenchmarkLastWeeksSuccess({
                  timeSeries: null,
                }),
                farmerInsightsActions.setNormalizedBenchmarkLastWeeksData({
                  normalizedBenchmarkData: null,
                }),
              );
            }
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            return of(
              farmerInsightsActions.loadBenchmarkTimeSeriesFailed({ error: error.message }),
              NotificationActions.errorSnackbar({ message: error.message }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  loadBenchmarkCustomerAverageSelectedPeriod$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadBenchmarkCustomerAverageSelectedPeriodRequest),
      exhaustMap((action) =>
        this.flockDataService
          .getCustomerAverageBenchmarks(action.getBenchmarkCustomerAverageSelectedPeriodRequest)
          .pipe(
            switchMap((timeSeriesResponse: TimeSeriesResponse) =>
              of(
                farmerInsightsActions.loadBenchmarkCustomerAverageSelectedPeriodSuccess({
                  timeSeries: timeSeriesResponse.data,
                }),
                farmerInsightsActions.convertToNormalizedBenchmarkData({
                  benchmarkType: BenchmarkTypes.CustomerAverageSelectedPeriod,
                  timeSeries: timeSeriesResponse.data,
                }),
              ),
            ),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 404) {
                return of(
                  farmerInsightsActions.loadBenchmarkCustomerAverageSelectedPeriodSuccess({
                    timeSeries: null,
                  }),
                  farmerInsightsActions.setNormalizedBenchmarkCustomerAverageSelectedPeriodData({
                    normalizedBenchmarkData: null,
                  }),
                );
              }
              this.store.dispatch(CoreActions.setLoading({ loading: false }));
              return of(
                farmerInsightsActions.loadBenchmarkTimeSeriesFailed({ error: error.message }),
                NotificationActions.errorSnackbar({ message: error.message }),
              );
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  convertToNormalizedTimeSeriesArray$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.convertToNormalizedTimeSeriesArray),
      withLatestFrom(
        this.store.select(getSelectedDataViewType),
        this.store.select(UserSelectors.selectLocations),
        this.store.select(getQueryableFlockData),
      ),
      exhaustMap(([{ timeSeries }, dataViewType, locations, queryableFlockData]) => {
        const normalizedTimeSeriesArray: NormalizedTimeSeries[] = [];
        if (timeSeries && queryableFlockData) {
          Object.assign([], timeSeries.metaData.parameters)
            .sort((a: DataSeriesParameter, b: DataSeriesParameter) =>
              <number>(<unknown>a.properties['Order']) > <number>(<unknown>b.properties['Order'])
                ? 1
                : -1,
            )
            .forEach((param: DataSeriesParameter) => {
              const timeSeriesDataArray = timeSeries.series[param.code];
              timeSeriesDataArray.forEach((timeSeriesData) => {
                let existingNormalizedTimeSeries: NormalizedTimeSeries;
                switch (dataViewType) {
                  case DataViewType.PerLocation:
                    existingNormalizedTimeSeries = normalizedTimeSeriesArray.find(
                      (d) =>
                        d.locationId === <number>(<unknown>timeSeriesData.properties['LocationId']),
                    );
                    break;
                  case DataViewType.PerCycle:
                    existingNormalizedTimeSeries = normalizedTimeSeriesArray.find(
                      (d) =>
                        d.locationId ===
                          <number>(<unknown>timeSeriesData.properties['LocationId']) &&
                        d.cycleId === <number>(<unknown>timeSeriesData.properties['CycleId']),
                    );
                    break;
                  case DataViewType.PerCycleBarn:
                    existingNormalizedTimeSeries = normalizedTimeSeriesArray.find(
                      (d) =>
                        d.locationId ===
                          <number>(<unknown>timeSeriesData.properties['LocationId']) &&
                        d.cycleId === <number>(<unknown>timeSeriesData.properties['CycleId']) &&
                        d.barnId === <number>(<unknown>timeSeriesData.properties['BarnId']),
                    );
                    break;
                  case DataViewType.PerLocationBarn:
                    existingNormalizedTimeSeries = normalizedTimeSeriesArray.find(
                      (d) =>
                        d.locationId ===
                          <number>(<unknown>timeSeriesData.properties['LocationId']) &&
                        d.barnId === <number>(<unknown>timeSeriesData.properties['BarnId']),
                    );
                    break;
                }
                if (existingNormalizedTimeSeries === undefined) {
                  existingNormalizedTimeSeries = {
                    kpiValues: [],
                    fromDate: undefined,
                    toDate: undefined,
                    breeds: undefined,
                  };
                  normalizedTimeSeriesArray.push(existingNormalizedTimeSeries);
                  if (timeSeriesData.properties['LocationId']) {
                    existingNormalizedTimeSeries.locationId = <number>(
                      (<unknown>timeSeriesData.properties['LocationId'])
                    );
                    const correspondingLocation = locations.find(
                      (d) => d.id === existingNormalizedTimeSeries.locationId,
                    );
                    if (correspondingLocation) {
                      existingNormalizedTimeSeries.locationName = correspondingLocation.name;
                    }
                  }
                  if (timeSeriesData.properties['CycleId']) {
                    existingNormalizedTimeSeries.cycleId = <number>(
                      (<unknown>timeSeriesData.properties['CycleId'])
                    );
                    const correspondingCycle = queryableFlockData.cycles.find(
                      (d) => d.cycleId === existingNormalizedTimeSeries.cycleId,
                    );
                    if (correspondingCycle) {
                      existingNormalizedTimeSeries.cycleNumber = correspondingCycle.cycleNumber;
                      existingNormalizedTimeSeries.fromDate = new Date(
                        correspondingCycle.cycleDate,
                      );
                      existingNormalizedTimeSeries.toDate = new Date(
                        correspondingCycle.cycleEndDate,
                      );
                    }
                    existingNormalizedTimeSeries.cycleNumber = <number>(
                      (<unknown>timeSeriesData.properties['CycleNumber'])
                    );
                  }
                  if (timeSeriesData.properties['BarnId']) {
                    existingNormalizedTimeSeries.barnId = <number>(
                      (<unknown>timeSeriesData.properties['BarnId'])
                    );
                    existingNormalizedTimeSeries.barnName = <string>(
                      (<unknown>timeSeriesData.properties['BarnName'])
                    );
                  }
                  if (timeSeriesData.properties['Breeds']) {
                    existingNormalizedTimeSeries.breeds = <string>(
                      (<unknown>timeSeriesData.properties['Breeds'])
                    );
                  }
                  if (timeSeriesData.properties['Breed']) {
                    existingNormalizedTimeSeries.breeds = <string>(
                      (<unknown>timeSeriesData.properties['Breed'])
                    );
                  }
                }

                if (timeSeriesData.values.length > 0) {
                  const metaDataParameter = timeSeries.metaData.parameters.find(
                    (d) => d.code === timeSeriesData.parameterCode,
                  );

                  const tsDataItem = timeSeriesData.values[0];
                  if (existingNormalizedTimeSeries.fromDate === undefined) {
                    existingNormalizedTimeSeries.fromDate = new Date(tsDataItem.dimensionValue);
                  } else if (
                    new Date(existingNormalizedTimeSeries.fromDate) >
                    new Date(tsDataItem.dimensionValue)
                  ) {
                    existingNormalizedTimeSeries.fromDate = new Date(tsDataItem.dimensionValue);
                  }
                  if (existingNormalizedTimeSeries.toDate === undefined) {
                    existingNormalizedTimeSeries.toDate = new Date(tsDataItem.dimensionValue);
                  } else if (
                    new Date(existingNormalizedTimeSeries.toDate) <
                    new Date(tsDataItem.dimensionValue)
                  ) {
                    existingNormalizedTimeSeries.toDate = new Date(tsDataItem.dimensionValue);
                  }

                  existingNormalizedTimeSeries.kpiValues.push({
                    parameter: metaDataParameter,
                    value: tsDataItem.value,
                    dateTime: new Date(tsDataItem.dimensionValue),
                  });
                }
              });
            });
        }
        return of(
          farmerInsightsActions.setNormalizedTimeSeriesArray({
            normalizedTimeSeriesArray,
          }),
        );
      }),
    ),
  );

  convertToNormalizedBenchmarkData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.convertToNormalizedBenchmarkData),
      exhaustMap(({ benchmarkType, timeSeries }) => {
        let benchmarkSelectedPeriodData: BenchmarkData = null;
        if (timeSeries) {
          benchmarkSelectedPeriodData = {
            flocksUsed: <number>(<unknown>timeSeries.metaData.other['FlocksUsed']),
            benchmarks: [],
          };
          for (const parameter of Object.keys(timeSeries.series)) {
            const timeSeriesMetaDataParam = timeSeries.metaData.parameters.find(
              (d) => d.code === parameter,
            );
            const timeSeriesDataArray = timeSeries.series[parameter];
            if (timeSeriesDataArray.length > 0 && timeSeriesMetaDataParam !== undefined) {
              const timeSeriesData = timeSeriesDataArray[0];
              if (timeSeriesData.values.length > 0) {
                benchmarkSelectedPeriodData.benchmarks.push({
                  parameter: timeSeriesMetaDataParam,
                  value: timeSeriesData.values[0].value,
                });
              }
            }
          }
        }

        switch (benchmarkType) {
          case BenchmarkTypes.SelectedPeriod:
            return of(
              farmerInsightsActions.setNormalizedBenchmarkSelectedPeriodData({
                normalizedBenchmarkData: benchmarkSelectedPeriodData,
              }),
            );
          case BenchmarkTypes.LastWeeks:
            return of(
              farmerInsightsActions.setNormalizedBenchmarkLastWeeksData({
                normalizedBenchmarkData: benchmarkSelectedPeriodData,
              }),
            );
          case BenchmarkTypes.CustomerAverageSelectedPeriod:
            return of(
              farmerInsightsActions.setNormalizedBenchmarkCustomerAverageSelectedPeriodData({
                normalizedBenchmarkData: benchmarkSelectedPeriodData,
              }),
            );
        }
      }),
    ),
  );

  loadFarmerInsightsBenchmarkSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadFarmerInsightsBenchmarkSettings),
      switchMap((_) =>
        this.settingsHelper
          .getSingle(
            {
              selectedNonDetailedBenchmarkType: BenchmarkTypes.SelectedPeriod,
              selectedPeriod: true,
              lastWeeks: false,
              customerAverageSelectedPeriod: false,
            } as BenchmarkSettings,
            PoultryContextKeys.FARMER_INSIGHTS_BENCHMARK_SETTINGS,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            map((v: BenchmarkSettings) => {
              if (!v.lastWeeks && !v.selectedPeriod && !v.customerAverageSelectedPeriod) {
                v.selectedPeriod = true;
              }

              return farmerInsightsActions.setFarmerInsightsBenchmarkSettings({
                settings: v,
                save: false,
              });
            }),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  loadFarmerInsightsLocalFilterSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadFarmerInsightsLocalFilterSettingsRequest),
      switchMap((_) =>
        this.settingsHelper
          .getSingle<LocalFilterSettings>(
            null,
            PoultryContextKeys.FARMER_INSIGHTS_LOCAL_FILTER_SETTINGS,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            mergeMap((localFilterSettings) =>
              of(
                farmerInsightsActions.loadFarmerInsightsLocalFilterSettingsSuccess({
                  localFilterSettings,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadFarmerInsightsGlobalFilterSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(farmerInsightsActions.loadFarmerInsightsGlobalFilterSettingsRequest),
      switchMap(() =>
        this.settingsHelper
          .getSingle<GlobalFilterSettings>(
            null,
            PoultryContextKeys.FARMER_INSIGHTS_GLOBAL_FILTER_SETTINGS,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            mergeMap((globalFilterSettings) =>
              of(
                farmerInsightsActions.loadFarmerInsightsGlobalFilterSettingsSuccess({
                  globalFilterSettings,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  setFarmerInsightsBenchmarkSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(farmerInsightsActions.setFarmerInsightsBenchmarkSettings),
        tap((action) => {
          if (action.save) {
            this.settingsHelper.setSettings(
              PoultryContextKeys.FARMER_INSIGHTS_BENCHMARK_SETTINGS,
              action.settings,
              SettingsStorage.Global,
              true,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  resetFarmerInsightsLocalFilterSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(farmerInsightsActions.resetFarmerInsightsLocalFilterSettings),
        switchMap((_) =>
          of(
            this.settingsHelper.removeSettings(
              PoultryContextKeys.FARMER_INSIGHTS_LOCAL_FILTER_SETTINGS,
              SettingsStorage.AppSession,
              false,
              true,
              false,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  resetFarmerInsightsGlobalFilterSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(farmerInsightsActions.resetFarmerInsightsGlobalFilterSettings),
        withLatestFrom(this.store.select(getFarmerInsightsModuleState)),
        switchMap(([_, state]) =>
          of(
            this.settingsHelper.setSettings(
              PoultryContextKeys.FARMER_INSIGHTS_GLOBAL_FILTER_SETTINGS,
              {
                selectedDataViewType: null,
                selectedKpis: [],
                isDetailedModeSelected: state.isDetailedModeSelected,
                selectedViewMode: state.selectedViewMode,
              },
              SettingsStorage.Global,
              true,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  setFarmerInsightsGlobalFilterSettings = createEffect(
    () =>
      this.actions$.pipe(
        ofType(farmerInsightsActions.setFarmerInsightsGlobalFilterSettings),
        switchMap((action) =>
          of(
            this.settingsHelper.setSettings(
              PoultryContextKeys.FARMER_INSIGHTS_GLOBAL_FILTER_SETTINGS,
              action.globalFilterSettings,
              SettingsStorage.Global,
              true,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  setFarmerInsightsLocalFilterSettings = createEffect(
    () =>
      this.actions$.pipe(
        ofType(farmerInsightsActions.setFarmerInsightsLocalFilterSettings),
        switchMap((action) =>
          of(
            this.settingsHelper.setSettings(
              PoultryContextKeys.FARMER_INSIGHTS_LOCAL_FILTER_SETTINGS,
              action.localFilterSettings,
              SettingsStorage.Global,
              true,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );
}
